import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  LogOut,
  Mail,
  Phone,
  Calendar,
  Trash2,
  Loader2,
  ShieldCheck,
  Link as LinkIcon,
  Inbox,
  Wallet,
  Copy,
  ExternalLink,
} from "lucide-react";
import Logo from "../components/Logo";
import PaymentLinkModal from "../components/PaymentLinkModal";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "zf_admin_token";

const SERVICE_LABEL = {
  pose_seule: "Pose seule",
  cle_en_main: "Clé en main",
  diagnostic: "Diagnostic",
  upgrade: "Upgrade",
  autre: "Autre",
};

const STATUS_LABEL = {
  new: "Nouveau",
  read: "Lu",
  contacted: "Contacté",
  closed: "Fermé",
};

const STATUS_COLOR = {
  new: "bg-violet-500/20 text-violet-300 border-violet-500/40",
  read: "bg-zinc-500/20 text-zinc-300 border-zinc-500/40",
  contacted: "bg-cyan-500/15 text-cyan-300 border-cyan-500/40",
  closed: "bg-emerald-500/15 text-emerald-300 border-emerald-500/40",
};

function Login({ onLogged }) {
  const [pwd, setPwd] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/admin/login`, { password: pwd });
      localStorage.setItem(TOKEN_KEY, data.token);
      onLogged(data.token);
      toast.success("Connexion réussie");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Connexion impossible");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4 bg-grid">
      <div className="absolute inset-0 bg-radial-violet pointer-events-none" />
      <form
        onSubmit={submit}
        data-testid="admin-login-form"
        className="relative w-full max-w-md gradient-border rounded-2xl p-8"
      >
        <div className="flex items-center gap-3">
          <ShieldCheck className="h-6 w-6 text-violet-300" />
          <h1 className="font-display text-2xl font-bold">Espace admin</h1>
        </div>
        <p className="mt-2 text-sm text-zinc-400">
          Connexion sécurisée pour consulter les demandes de devis.
        </p>

        <label className="block mt-6 text-xs text-zinc-400">Mot de passe</label>
        <Input
          type="password"
          autoFocus
          data-testid="admin-password"
          value={pwd}
          onChange={(e) => setPwd(e.target.value)}
          placeholder="••••••••"
          className="mt-2 bg-zinc-900/60 border-white/10 focus-visible:ring-violet-500 text-white"
        />

        <button
          type="submit"
          disabled={loading || !pwd}
          data-testid="admin-login-submit"
          className="mt-6 w-full inline-flex items-center justify-center gap-2 rounded-md bg-violet-600 hover:bg-violet-500 disabled:opacity-60 px-5 py-2.5 text-sm font-semibold text-white shadow-violet"
        >
          {loading ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            "Se connecter"
          )}
        </button>
      </form>
    </div>
  );
}

function Dashboard({ token, onLogout }) {
  const [tab, setTab] = useState("quotes"); // quotes | payments
  const [items, setItems] = useState([]);
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [modalQuote, setModalQuote] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);

  const headers = { Authorization: `Bearer ${token}` };

  const load = async () => {
    setLoading(true);
    try {
      const [q, p] = await Promise.all([
        axios.get(`${API}/admin/quotes`, { headers }),
        axios.get(`${API}/admin/payment-links`, { headers }),
      ]);
      setItems(q.data);
      setPayments(p.data);
    } catch (err) {
      if (err?.response?.status === 401) {
        toast.error("Session expirée");
        onLogout();
      } else {
        toast.error("Erreur de chargement");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line
  }, []);

  const updateStatus = async (id, status) => {
    try {
      const { data } = await axios.patch(
        `${API}/admin/quotes/${id}`,
        { status },
        { headers }
      );
      setItems((arr) => arr.map((q) => (q.id === id ? data : q)));
      toast.success("Statut mis à jour");
    } catch {
      toast.error("Échec mise à jour");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Supprimer définitivement ce devis ?")) return;
    try {
      await axios.delete(`${API}/admin/quotes/${id}`, { headers });
      setItems((arr) => arr.filter((q) => q.id !== id));
      toast.success("Devis supprimé");
    } catch {
      toast.error("Échec suppression");
    }
  };

  const removePayment = async (id) => {
    if (!window.confirm("Supprimer ce lien de paiement ?")) return;
    try {
      await axios.delete(`${API}/admin/payment-links/${id}`, { headers });
      setPayments((arr) => arr.filter((p) => p.id !== id));
      toast.success("Lien supprimé");
    } catch {
      toast.error("Échec suppression");
    }
  };

  const openPaymentModal = (quote = null) => {
    setModalQuote(quote);
    setModalOpen(true);
  };

  const onPaymentCreated = (pl) => {
    setPayments((arr) => [pl, ...arr]);
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/admin/logout`, {}, { headers });
    } catch (e) {
      // ignore
    }
    onLogout();
  };

  const filtered =
    filter === "all" ? items : items.filter((i) => i.status === filter);

  return (
    <div className="min-h-screen">
      <header className="sticky top-0 z-30 border-b border-white/5 bg-black/70 backdrop-blur-xl">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-6">
            <Logo />
            <span className="hidden sm:inline text-xs font-mono uppercase tracking-[0.18em] text-zinc-500">
              · Admin
            </span>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => openPaymentModal(null)}
              data-testid="new-payment-link-btn"
              className="hidden sm:inline-flex items-center gap-2 rounded-md bg-violet-600 hover:bg-violet-500 px-3 py-2 text-xs font-semibold text-white shadow-violet"
            >
              <LinkIcon className="h-3.5 w-3.5" />
              Nouveau lien
            </button>
            <button
              onClick={logout}
              data-testid="admin-logout"
              className="inline-flex items-center gap-2 rounded-md border border-white/10 bg-white/5 hover:bg-white/10 px-3 py-2 text-xs"
            >
              <LogOut className="h-3.5 w-3.5" />
              Déconnexion
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-10">
        {/* Tabs */}
        <div className="flex items-center gap-1 rounded-lg border border-white/10 bg-zinc-900/40 p-1 w-fit">
          <button
            onClick={() => setTab("quotes")}
            data-testid="tab-quotes"
            className={`inline-flex items-center gap-2 px-4 py-2 rounded-md text-sm transition-all ${
              tab === "quotes"
                ? "bg-violet-600 text-white shadow-violet-soft"
                : "text-zinc-400 hover:text-white"
            }`}
          >
            <Inbox className="h-3.5 w-3.5" /> Devis
            <span className="ml-1 text-[10px] font-mono opacity-70">
              {items.length}
            </span>
          </button>
          <button
            onClick={() => setTab("payments")}
            data-testid="tab-payments"
            className={`inline-flex items-center gap-2 px-4 py-2 rounded-md text-sm transition-all ${
              tab === "payments"
                ? "bg-violet-600 text-white shadow-violet-soft"
                : "text-zinc-400 hover:text-white"
            }`}
          >
            <Wallet className="h-3.5 w-3.5" /> Paiements
            <span className="ml-1 text-[10px] font-mono opacity-70">
              {payments.length}
            </span>
          </button>
        </div>

        {tab === "quotes" ? (
          <QuotesPanel
            items={items}
            filtered={filtered}
            loading={loading}
            filter={filter}
            setFilter={setFilter}
            updateStatus={updateStatus}
            remove={remove}
            openPaymentModal={openPaymentModal}
          />
        ) : (
          <PaymentsPanel
            payments={payments}
            loading={loading}
            removePayment={removePayment}
            openPaymentModal={() => openPaymentModal(null)}
          />
        )}
      </main>

      {modalOpen && (
        <PaymentLinkModal
          quote={modalQuote}
          token={token}
          onClose={() => setModalOpen(false)}
          onCreated={onPaymentCreated}
        />
      )}
    </div>
  );
}

function QuotesPanel({
  items,
  filtered,
  loading,
  filter,
  setFilter,
  updateStatus,
  remove,
  openPaymentModal,
}) {
  return (
    <>
      <div className="mt-8 flex items-end justify-between gap-4 flex-wrap">
        <div>
          <p className="font-mono text-xs uppercase tracking-[0.22em] text-violet-400">
            // Demandes de devis
          </p>
          <h1 className="mt-3 font-display text-3xl sm:text-4xl font-bold">
            {items.length} demande{items.length > 1 ? "s" : ""} au total
          </h1>
        </div>
        <div className="w-48">
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger
              data-testid="filter-status"
              className="bg-zinc-900/60 border-white/10 text-white"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-zinc-900 border-white/10 text-white">
              <SelectItem value="all">Tous</SelectItem>
              <SelectItem value="new">Nouveaux</SelectItem>
              <SelectItem value="read">Lus</SelectItem>
              <SelectItem value="contacted">Contactés</SelectItem>
              <SelectItem value="closed">Fermés</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {loading ? (
        <div className="mt-12 flex items-center gap-2 text-zinc-400">
          <Loader2 className="h-4 w-4 animate-spin" /> Chargement…
        </div>
      ) : filtered.length === 0 ? (
        <div
          data-testid="empty-state"
          className="mt-12 rounded-2xl border border-dashed border-white/10 p-12 text-center text-zinc-500"
        >
          Aucune demande pour le moment.
        </div>
      ) : (
        <div className="mt-10 grid lg:grid-cols-2 gap-4">
          {filtered.map((q) => (
            <article
              key={q.id}
              data-testid={`quote-card-${q.id}`}
              className="rounded-2xl border border-white/10 bg-zinc-900/40 p-6 hover:border-violet-400/40 transition-all"
            >
              <div className="flex items-start justify-between gap-3">
                <div>
                  <h3 className="font-display text-lg font-semibold">
                    {q.name}
                  </h3>
                  <p className="mt-1 text-xs text-zinc-500 font-mono">
                    {SERVICE_LABEL[q.service_type] || q.service_type}
                    {q.budget ? ` · ${q.budget}` : ""}
                  </p>
                </div>
                <span
                  className={`px-2 py-1 rounded-full text-[10px] font-mono uppercase tracking-wider border ${
                    STATUS_COLOR[q.status]
                  }`}
                >
                  {STATUS_LABEL[q.status]}
                </span>
              </div>

              <p className="mt-4 text-sm text-zinc-300 leading-relaxed whitespace-pre-wrap line-clamp-6">
                {q.message}
              </p>

              <div className="mt-5 space-y-1.5 text-xs">
                <a
                  href={`mailto:${q.email}`}
                  className="flex items-center gap-2 text-zinc-400 hover:text-violet-300"
                >
                  <Mail className="h-3.5 w-3.5" />
                  {q.email}
                </a>
                <a
                  href={`tel:${q.phone}`}
                  className="flex items-center gap-2 text-zinc-400 hover:text-violet-300"
                >
                  <Phone className="h-3.5 w-3.5" />
                  {q.phone}
                </a>
                <p className="flex items-center gap-2 text-zinc-500">
                  <Calendar className="h-3.5 w-3.5" />
                  {new Date(q.created_at).toLocaleString("fr-FR")}
                </p>
              </div>

              <div className="mt-5 flex flex-wrap items-center gap-2">
                <Select
                  value={q.status}
                  onValueChange={(v) => updateStatus(q.id, v)}
                >
                  <SelectTrigger
                    data-testid={`status-select-${q.id}`}
                    className="h-9 w-36 bg-zinc-900/60 border-white/10 text-white text-xs"
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-900 border-white/10 text-white">
                    <SelectItem value="new">Nouveau</SelectItem>
                    <SelectItem value="read">Lu</SelectItem>
                    <SelectItem value="contacted">Contacté</SelectItem>
                    <SelectItem value="closed">Fermé</SelectItem>
                  </SelectContent>
                </Select>
                <button
                  onClick={() => openPaymentModal(q)}
                  data-testid={`gen-payment-${q.id}`}
                  className="inline-flex items-center gap-1.5 rounded-md bg-violet-600 hover:bg-violet-500 px-3 py-2 text-xs font-semibold text-white shadow-violet-soft"
                >
                  <LinkIcon className="h-3.5 w-3.5" /> Lien de paiement
                </button>
                <button
                  onClick={() => remove(q.id)}
                  data-testid={`delete-btn-${q.id}`}
                  className="inline-flex items-center gap-1.5 rounded-md border border-red-500/30 bg-red-500/10 hover:bg-red-500/20 px-3 py-2 text-xs text-red-300"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </div>
            </article>
          ))}
        </div>
      )}
    </>
  );
}

function PaymentsPanel({ payments, loading, removePayment, openPaymentModal }) {
  const copy = async (slug) => {
    await navigator.clipboard.writeText(`${window.location.origin}/paiement/${slug}`);
    toast.success("Lien copié");
  };

  const total = payments
    .filter((p) => p.status === "paid")
    .reduce((s, p) => s + p.amount, 0);

  const pendingCount = payments.filter((p) => p.status !== "paid").length;

  return (
    <>
      <div className="mt-8 flex items-end justify-between gap-4 flex-wrap">
        <div>
          <p className="font-mono text-xs uppercase tracking-[0.22em] text-violet-400">
            // Liens de paiement
          </p>
          <h1 className="mt-3 font-display text-3xl sm:text-4xl font-bold">
            {total.toFixed(2)}€{" "}
            <span className="text-zinc-500 text-base font-normal">
              encaissés · {pendingCount} en attente
            </span>
          </h1>
        </div>
        <button
          onClick={openPaymentModal}
          data-testid="payments-new-link-btn"
          className="inline-flex items-center gap-2 rounded-md bg-violet-600 hover:bg-violet-500 px-4 py-2.5 text-sm font-semibold text-white shadow-violet"
        >
          <LinkIcon className="h-4 w-4" />
          Nouveau lien de paiement
        </button>
      </div>

      {loading ? (
        <div className="mt-12 flex items-center gap-2 text-zinc-400">
          <Loader2 className="h-4 w-4 animate-spin" /> Chargement…
        </div>
      ) : payments.length === 0 ? (
        <div
          data-testid="payments-empty"
          className="mt-12 rounded-2xl border border-dashed border-white/10 p-12 text-center text-zinc-500"
        >
          Aucun lien de paiement pour le moment. Crées-en un pour ton premier client.
        </div>
      ) : (
        <div className="mt-10 grid lg:grid-cols-2 gap-4">
          {payments.map((p) => (
            <article
              key={p.id}
              data-testid={`payment-card-${p.id}`}
              className="rounded-2xl border border-white/10 bg-zinc-900/40 p-6 hover:border-violet-400/40 transition-all"
            >
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className="font-display text-3xl font-extrabold">
                    {p.amount.toFixed(2)}€
                  </p>
                  <p className="mt-1 text-sm text-zinc-400">{p.description}</p>
                </div>
                <span
                  className={`px-2 py-1 rounded-full text-[10px] font-mono uppercase tracking-wider border ${
                    p.status === "paid"
                      ? "bg-emerald-500/15 text-emerald-300 border-emerald-500/40"
                      : "bg-violet-500/15 text-violet-300 border-violet-500/40"
                  }`}
                >
                  {p.status === "paid" ? "Payé" : "En attente"}
                </span>
              </div>

              {p.client_name && (
                <p className="mt-3 text-xs text-zinc-500">
                  Client :{" "}
                  <span className="text-zinc-300">{p.client_name}</span>
                  {p.client_email ? ` · ${p.client_email}` : ""}
                </p>
              )}

              <div className="mt-5 rounded-md border border-violet-500/30 bg-violet-500/5 p-3 flex items-center gap-2">
                <code className="flex-1 text-[11px] text-violet-200 break-all">
                  {window.location.origin}/paiement/{p.slug}
                </code>
                <button
                  onClick={() => copy(p.slug)}
                  data-testid={`copy-${p.id}`}
                  className="flex h-7 w-7 items-center justify-center rounded-md bg-violet-600 hover:bg-violet-500"
                  title="Copier"
                >
                  <Copy className="h-3.5 w-3.5" />
                </button>
                <a
                  href={`/paiement/${p.slug}`}
                  target="_blank"
                  rel="noreferrer"
                  className="flex h-7 w-7 items-center justify-center rounded-md border border-white/10 bg-white/5 hover:bg-white/10"
                  title="Ouvrir"
                >
                  <ExternalLink className="h-3.5 w-3.5" />
                </a>
              </div>

              <div className="mt-4 flex items-center justify-between text-[11px] text-zinc-500">
                <span className="flex items-center gap-1.5">
                  <Calendar className="h-3 w-3" />
                  Créé le{" "}
                  {new Date(p.created_at).toLocaleDateString("fr-FR")}
                </span>
                <button
                  onClick={() => removePayment(p.id)}
                  data-testid={`payment-delete-${p.id}`}
                  className="inline-flex items-center gap-1 text-red-400 hover:text-red-300"
                >
                  <Trash2 className="h-3 w-3" /> Supprimer
                </button>
              </div>
            </article>
          ))}
        </div>
      )}
    </>
  );
}

export default function Admin() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY));

  useEffect(() => {
    document.title = "Admin · ZF Industries";
    let robots = document.querySelector('meta[name="robots"]');
    if (!robots) {
      robots = document.createElement("meta");
      robots.setAttribute("name", "robots");
      document.head.appendChild(robots);
    }
    robots.setAttribute("content", "noindex, nofollow");
    return () => robots.setAttribute("content", "index, follow, max-image-preview:large");
  }, []);

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken(null);
  };

  return token ? (
    <Dashboard token={token} onLogout={logout} />
  ) : (
    <Login onLogged={setToken} />
  );
}
